import FavoriteIcon from "components/FavoriteIcon";
import LazyLoadImg from "components/LazyLoadImg";
import { RouteName } from "constant/routeNames";
import { FaPlay } from "react-icons/fa";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { FaCrown } from "react-icons/fa";
import "styles/card.css";
import { usePremiumAccess } from "hooks/usePremiumAccess";
import PremiumContentModal from "components/PremiumContentModal";

function MovieCarouselCard({
  id,
  time,
  runtime,
  user,
  title,
  info,
  year,
  img,
  slug,
  tv,
  seasons,
  thumbnail,
  movie,
  tvSeries,
  searchPage,
  first_episode,
  plan_id,
  is_creator,
}: any) {
  // used in every coursel
  const navigate = useNavigate();
  const { isModalOpen, setIsModalOpen, checkAccess } = usePremiumAccess();

  // const [isLiked, setIsLiked] = useState<boolean>();
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);

  const isPremiumContent = plan_id === 2;
  const isCreatorSeries =
    tvSeries?.type === "creator_shows" ||
    tvSeries?.tv_show_type === "creator_shows";

  const setDataToPlayer = async () => {
    if (
      isCreatorSeries &&
      (!tvSeries?.creator_artist_slug || !first_episode?.stream_key)
    ) {
      console.error(
        "Creator series is missing its slug or first episode stream key:",
        tvSeries,
      );
      return;
    }

    const access = await checkAccess({
      slug,
      isPremiumContent,
      isCreator: is_creator || isCreatorSeries,
    });
    if (!access) return;

    const state = { searchPage: searchPage ? searchPage : false };
    if (isCreatorSeries) {
      navigate(
        RouteName.CreatorPlaylist.replace(
          ":slug",
          tvSeries.creator_artist_slug,
        ).replace(":stream", first_episode.stream_key),
        { state },
      );
    } else if (first_episode) {
      navigate(
        RouteName.WatchTVSeries.replace(":slug", slug).replace(
          ":stream",
          first_episode?.stream_key,
        ),
        { state },
      );
    } else {
      navigate(RouteName.WatchMovies.replace(":slug", slug), { state });
    }
  };

  const contentData = movie || tvSeries || {};
  const packageObject: any = {
    id: contentData?.service_id || id,
    entitlement_id: contentData?.entitlement_id || "",
    title: title || info || contentData?.title,
    price: contentData?.price || "",
    thumbnail: img || thumbnail || contentData?.thumbnail_url || "",
    created_at: contentData?.created_at || "",
    description: contentData?.description || info || "",
    is_subscribed: contentData?.is_Subscribed || false,
    role_id: plan_id || contentData?.plan_id,
    slug: slug || contentData?.slug,
  };

  return (
    <>
      <PremiumContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isCreator={is_creator}
        packageObject={packageObject}
      />

      <div className="group relative mx-auto w-[190px] cursor-pointer transition-transform duration-300 ease-out hover:z-10 hover:-translate-y-1 max-sm:w-[180px]">
        {/* Poster */}
        <div className="bg-img relative aspect-[2/3] w-full overflow-hidden rounded-2xl bg-[#1a1a1a] ring-1 ring-white/5 transition-all duration-300 group-hover:ring-2 group-hover:ring-[var(--red)]/60 group-hover:shadow-2xl group-hover:shadow-[var(--red)]/20">
          <LazyLoadImg
            imgSrc={img || thumbnail}
            imgAlt={"Card pic"}
            imgHeight={"100%"}
            imgWidth={"100%"}
            wrapperProps={{
              style: {
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                display: "block",
              },
            }}
            className="object-cover w-full h-full transition-transform duration-500 ease-out group-hover:scale-110"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />

          {/* Bottom gradient for legibility */}
          <div className="absolute inset-0 transition-opacity duration-300 pointer-events-none bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-80 group-hover:opacity-100" />

          {/* Premium badge (glassy) */}
          {isPremiumContent && (
            <div className="absolute left-2.5 top-2.5 z-20 flex items-center gap-1 rounded-full border border-white/15 bg-black/40 px-2 py-1 text-[10px] font-semibold text-white backdrop-blur-md">
              <FaCrown className="text-[var(--red)] text-[10px]" />
              <span>PREMIUM</span>
            </div>
          )}

          {/* Center play button — reveals on hover */}
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
            <button
              onClick={setDataToPlayer}
              aria-label="Play"
              className="pointer-events-auto flex h-12 w-12 scale-75 items-center justify-center rounded-full bg-[var(--red)] text-white opacity-0 shadow-xl shadow-[var(--red)]/40 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100 hover:!scale-110 active:!scale-95"
            >
              <FaPlay className="translate-x-[1px] text-sm" />
            </button>
          </div>

          {/* Meta chip (runtime / seasons) */}
          <div className="absolute flex-row inset-x-0 bottom-0 z-10 flex items-end justify-between p-2.5">
            {loggedInUser &&
              (user ? (
                <>
                  {loggedInUser && user?.id === loggedInUser?.id && (
                    <FavoriteIcon
                      seasons={seasons}
                      id={id}
                      title={title}
                      movie={movie}
                      tvSeries={tvSeries}
                      VideoCard={true}
                    />
                  )}
                </>
              ) : (
                <>
                  <FavoriteIcon
                    seasons={seasons}
                    id={id}
                    title={title}
                    movie={movie}
                    tvSeries={tvSeries}
                    VideoCard={true}
                  />
                </>
              ))}
            {(runtime || time) && (
              <span className="rounded-md bg-black/50 px-1.5 py-0.5 text-[11px] font-medium text-slate-100 backdrop-blur-sm">
                {Math.floor(time / 60) > 0 && `${Math.floor(time / 60)}h `}
                {Math.floor(time % 60) > 0 && `${Math.floor(time % 60)}min`}
              </span>
            )}
            {seasons && (
              <span className="rounded-md bg-black/50 px-1.5 py-0.5 text-[11px] font-medium text-slate-100 backdrop-blur-sm">
                {seasons} seasons
              </span>
            )}
          </div>
        </div>

        {/* Title / year */}
        <div className="mt-2.5 px-0.5">
          {tv === 0 ? (
            <h2 className="line-clamp-2 text-[13px] font-semibold leading-tight text-slate-200 transition-colors group-hover:text-white">
              {info}
            </h2>
          ) : (
            <Link to={RouteName.VideoDetailRoute.replace(":slug", slug)}>
              <h2
                className="line-clamp-2 cursor-pointer text-[15px] font-semibold leading-tight text-slate-200 transition-colors group-hover:text-white"
                data-tooltip-id={
                  title?.length > 45 ? `add-song-to-favorite-${slug}` : ""
                }
              >
                {title}
              </h2>
              <div className="z-50 text-sm">
                <ReactTooltip
                  id={`add-song-to-favorite-${slug}`}
                  place="bottom"
                  content={title}
                  style={{ width: "180px" }}
                />
              </div>
            </Link>
          )}
          {year !== "-1" && (
            <p className="gray-text mt-0.5 text-[11px] text-slate-400">
              {year}
            </p>
          )}
        </div>
      </div>
    </>
  );
}

export default MovieCarouselCard;
